import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import Logo from '../components/Logo';
import API_URL from '../config/api';

const AdminTemplateEditor = () => {
  const { id } = useParams();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Edit states
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(`${API_URL}/api/survey-templates/${id}`, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.template) {
        setTemplate(response.data.template);
      }
    } catch (err) {
      console.error('Error fetching template:', err);
      setError('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/survey-templates/${id}`, template, {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        }
      });

      if (response.data?.template) {
        setTemplate(response.data.template);
        setSuccessMessage('Template saved successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template: ' + (err.response?.data?.details || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = (categoryId, field, value) => {
    setTemplate(prev => ({
      ...prev,
      categories: prev.categories.map(cat =>
        cat.id === categoryId ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const handleUpdateQuestion = (questionId, field, value) => {
    setTemplate(prev => ({
      ...prev,
      questions: prev.questions.map(q =>
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleAddQuestion = (categoryId) => {
    const newQuestionId = Math.max(...template.questions.map(q => q.id), 0) + 1;
    const category = template.categories.find(c => c.id === categoryId);

    const newQuestion = {
      id: newQuestionId,
      text: 'New question text...',
      category: categoryId,
      weightFactor: 1.5,
      improvementSuggestion: 'Add improvement suggestion...'
    };

    setTemplate(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
      categories: prev.categories.map(cat =>
        cat.id === categoryId
          ? { ...cat, questions: [...cat.questions, newQuestionId] }
          : cat
      )
    }));

    setEditingQuestion(newQuestionId);
  };

  const handleDeleteQuestion = (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) {
      return;
    }

    const question = template.questions.find(q => q.id === questionId);

    setTemplate(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId),
      categories: prev.categories.map(cat =>
        cat.id === question.category
          ? { ...cat, questions: cat.questions.filter(qId => qId !== questionId) }
          : cat
      )
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Logo />
          <button onClick={handleLogout} style={{
            backgroundColor: '#DC0032',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading template...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Logo />
          <button onClick={handleLogout} style={{
            backgroundColor: '#DC0032',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
        <div style={{ textAlign: 'center', marginTop: '50px', color: 'red' }}>Template not found</div>
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Link to="/admin/templates" style={{ color: '#DC0032' }}>← Back to Templates</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Logo />
        <div>
          <span style={{ marginRight: '20px' }}>Admin: {user?.fullName}</span>
          <button onClick={handleLogout} style={{
            backgroundColor: '#DC0032',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Logout
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ marginBottom: '20px' }}>
        <Link to="/admin/templates" style={{ color: '#DC0032', textDecoration: 'none', marginRight: '10px' }}>
          ← Back to Templates
        </Link>
      </div>

      {/* Title and Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1 style={{ color: '#2D2D2D', margin: 0 }}>Edit Template: {template.name}</h1>
          <div style={{ marginTop: '8px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{
              backgroundColor: template.assessmentType === 'ai' ? '#7c3aed' : '#0ea5e9',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {template.assessmentType === 'ai' ? 'AI Maturity' : 'Digital Transformation'}
            </span>
            <span style={{ color: '#666', fontSize: '14px' }}>Version: {template.version}</span>
            {template.isActive && (
              <span style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                ACTIVE
              </span>
            )}
          </div>
        </div>

        <button
          onClick={handleSaveTemplate}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#6c757d' : '#28a745',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: '500'
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          backgroundColor: '#FFF5F7',
          color: '#DC0032',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          border: '1px solid #DC0032'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {/* Warning for active templates */}
      {template.isActive && (
        <div style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '15px',
          borderRadius: '5px',
          marginBottom: '20px',
          border: '1px solid #ffeaa7'
        }}>
          ⚠️ <strong>Warning:</strong> This is an active template. Changes will affect all new assessments immediately. Consider cloning this template to create a new version instead.
        </div>
      )}

      {/* Categories and Questions */}
      <div style={{ marginTop: '30px' }}>
        {template.categories.map((category, catIndex) => (
          <div key={category.id} style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {/* Category Header */}
            <div style={{ marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #e9ecef' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  {editingCategory === category.id ? (
                    <div>
                      <input
                        type="text"
                        value={category.name}
                        onChange={(e) => handleUpdateCategory(category.id, 'name', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          fontSize: '20px',
                          fontWeight: '600',
                          border: '2px solid #DC0032',
                          borderRadius: '4px',
                          marginBottom: '10px'
                        }}
                      />
                      <textarea
                        value={category.description}
                        onChange={(e) => handleUpdateCategory(category.id, 'description', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '2px solid #DC0032',
                          borderRadius: '4px',
                          minHeight: '60px'
                        }}
                      />
                      <button
                        onClick={() => setEditingCategory(null)}
                        style={{
                          marginTop: '10px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <div>
                      <h2 style={{ color: '#2D2D2D', margin: '0 0 8px 0' }}>
                        {catIndex + 1}. {category.name}
                      </h2>
                      <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>{category.description}</p>
                    </div>
                  )}
                </div>
                {editingCategory !== category.id && (
                  <button
                    onClick={() => setEditingCategory(category.id)}
                    style={{
                      backgroundColor: '#17a2b8',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Edit Category
                  </button>
                )}
              </div>
            </div>

            {/* Questions in this category */}
            <div>
              <h3 style={{ color: '#2D2D2D', fontSize: '16px', marginBottom: '15px' }}>
                Questions ({category.questions?.length || 0})
              </h3>

              {template.questions
                .filter(q => q.category === category.id)
                .sort((a, b) => a.id - b.id)
                .map((question, qIndex) => (
                  <div key={question.id} style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '5px',
                    marginBottom: '15px',
                    border: editingQuestion === question.id ? '2px solid #DC0032' : '1px solid #e9ecef'
                  }}>
                    {editingQuestion === question.id ? (
                      <div>
                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                            Question Text:
                          </label>
                          <textarea
                            value={question.text}
                            onChange={(e) => handleUpdateQuestion(question.id, 'text', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              minHeight: '80px'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '10px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                            Weight Factor (1.0 - 3.0):
                          </label>
                          <input
                            type="number"
                            step="0.5"
                            min="1.0"
                            max="3.0"
                            value={question.weightFactor}
                            onChange={(e) => handleUpdateQuestion(question.id, 'weightFactor', parseFloat(e.target.value))}
                            style={{
                              width: '150px',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                          />
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500', fontSize: '12px', color: '#666' }}>
                            Improvement Suggestion:
                          </label>
                          <textarea
                            value={question.improvementSuggestion || ''}
                            onChange={(e) => handleUpdateQuestion(question.id, 'improvementSuggestion', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              minHeight: '60px'
                            }}
                          />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            onClick={() => setEditingQuestion(null)}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Done Editing
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Delete Question
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                            Q{question.id}: {question.text}
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            Weight: {question.weightFactor} | Suggestion: {question.improvementSuggestion?.substring(0, 50)}...
                          </div>
                        </div>
                        <button
                          onClick={() => setEditingQuestion(question.id)}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                ))}

              {/* Add Question Button */}
              <button
                onClick={() => handleAddQuestion(category.id)}
                style={{
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  width: '100%',
                  fontWeight: '500'
                }}
              >
                + Add New Question to {category.name}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Save Button at Bottom */}
      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px', textAlign: 'center' }}>
        <button
          onClick={handleSaveTemplate}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#6c757d' : '#DC0032',
            color: 'white',
            border: 'none',
            padding: '15px 40px',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '18px',
            fontWeight: '600'
          }}
        >
          {saving ? 'Saving Changes...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
};

export default AdminTemplateEditor;
